import { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Gift, TrendingUp, Sparkles, Info, ChartBar, Save } from 'lucide-react';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DashboardCashback() {
    const [config, setConfig] = useState({ enabled: false, percent: 3, min_order_amount: 0, max_cashback_per_order: null });
    const [global, setGlobal] = useState({ global_max_percent: 15, global_enabled: true });
    const [stats, setStats] = useState({ total_cashback_given: 0, cashback_transactions: 0 });
    const [busy, setBusy] = useState(false);
    const [simTicket, setSimTicket] = useState(500);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/me/cashback');
            setConfig(data.config || {});
            setGlobal(data.global || {});
            setStats(data.stats || {});
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                enabled: !!config.enabled,
                percent: Math.min(parseFloat(config.percent) || 0, global.global_max_percent),
                min_order_amount: parseFloat(config.min_order_amount) || 0,
                max_cashback_per_order: config.max_cashback_per_order ? parseFloat(config.max_cashback_per_order) : null,
            };
            await api.put('/me/cashback', payload);
            toast.success('Configuración de cashback guardada');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
    };

    const sim = useMemo(() => {
        const pct = Math.min(parseFloat(config.percent) || 0, global.global_max_percent);
        const cashback = simTicket * (pct / 100);
        const cap = config.max_cashback_per_order ? parseFloat(config.max_cashback_per_order) : null;
        const finalCashback = cap != null ? Math.min(cashback, cap) : cashback;
        return {
            cashback: finalCashback,
            yourNet: simTicket - finalCashback,
            buyerEffective: simTicket - finalCashback,
            capped: cap != null && cashback > cap,
        };
    }, [simTicket, config, global]);

    return (
        <div className="space-y-6" data-testid="dashboard-cashback">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Gift className="h-6 w-6 text-[hsl(var(--primary))]" />
                    <h1 className="text-2xl font-semibold">Cashback para tu tienda</h1>
                    {config.enabled && <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge>}
                </div>
            </div>

            {/* Motivational */}
            <Card className="overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-[hsl(var(--primary))] to-red-700" />
                <CardContent className="p-5 space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="h-10 w-10 rounded-lg bg-[hsl(var(--accent))] flex items-center justify-center shrink-0">
                            <TrendingUp className="h-5 w-5 text-[hsl(var(--primary))]" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold">Vende más ofreciendo cashback</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                El cashback es un <strong>porcentaje que tú como vendedor absorbes</strong> de cada venta y se devuelve al comprador como <strong>puntos OkWallet</strong>. Esos puntos incentivan a que vuelva a comprarte, mejoran tu ranking y aumentan tu tasa de conversión.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                        <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                            <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3 w-3" />Más conversión</div>
                            <div className="text-sm mt-1">Compradores con cashback compran <strong>hasta 30% más</strong> a vendedores que lo ofrecen.</div>
                        </div>
                        <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                            <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3 w-3" />Cliente recurrente</div>
                            <div className="text-sm mt-1">Los puntos se pueden canjear después en cualquier compra: te vuelven a comprar a ti primero.</div>
                        </div>
                        <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                            <div className="flex items-center gap-1 text-xs uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3 w-3" />Diferenciación</div>
                            <div className="text-sm mt-1">Aparece badge “🎁 Cashback” en tu anuncio, aumentando el CTR.</div>
                        </div>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                        <Info className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>Recuerda: <strong>el costo del cashback lo asumes tú</strong>. Se descuenta de tu neto por venta después de la comisión de plataforma. Ajusta tu margen antes de activarlo.</span>
                    </div>
                </CardContent>
            </Card>

            {/* Config */}
            <Card>
                <CardContent className="p-5 space-y-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-semibold">Activar cashback en mi tienda</h3>
                            <p className="text-xs text-muted-foreground mt-1">Máximo permitido por la plataforma: <strong>{global.global_max_percent}%</strong></p>
                        </div>
                        <Switch checked={!!config.enabled} onCheckedChange={(v) => setConfig({ ...config, enabled: v })} data-testid="cashback-enable-switch" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-3">
                            <Label>Porcentaje de cashback ({(parseFloat(config.percent) || 0).toFixed(1)}%)</Label>
                            <Slider
                                value={[Math.min(parseFloat(config.percent) || 0, global.global_max_percent)]}
                                min={0} max={global.global_max_percent} step={0.5}
                                onValueChange={(v) => setConfig({ ...config, percent: v[0] })}
                                disabled={!config.enabled}
                                data-testid="cashback-percent-slider"
                            />
                        </div>
                        <div>
                            <Label>Monto mínimo de la orden</Label>
                            <Input type="number" min="0" value={config.min_order_amount || ''} onChange={(e) => setConfig({ ...config, min_order_amount: e.target.value })} placeholder="0" data-testid="cashback-min-amount" />
                        </div>
                        <div>
                            <Label>Cashback máx por orden (opcional)</Label>
                            <Input type="number" min="0" value={config.max_cashback_per_order || ''} onChange={(e) => setConfig({ ...config, max_cashback_per_order: e.target.value })} placeholder="Sin límite" data-testid="cashback-max-per-order" />
                        </div>
                        <div className="flex items-end">
                            <Button onClick={save} disabled={busy} className="w-full" data-testid="cashback-save">
                                <Save className="h-4 w-4 mr-1" />{busy ? 'Guardando…' : 'Guardar cambios'}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Simulator */}
            <Card>
                <CardContent className="p-5 space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                        <ChartBar className="h-5 w-5 text-[hsl(var(--primary))]" />
                        <h3 className="text-base font-semibold">Simulador de venta</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">Prueba cómo se vería tu venta con la configuración actual.</p>
                    <div>
                        <Label>Ticket de la venta (MXN)</Label>
                        <Input type="number" value={simTicket} onChange={(e) => setSimTicket(parseFloat(e.target.value) || 0)} className="max-w-xs" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
                        <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                            <div className="text-xs uppercase text-emerald-700">Cashback al comprador</div>
                            <div className="text-2xl font-semibold text-emerald-800">{fmt(sim.cashback)} pts</div>
                            {sim.capped && <div className="text-[10px] text-emerald-700">Aplicado tope máx por orden</div>}
                        </div>
                        <div className="p-4 rounded-lg bg-slate-50 border">
                            <div className="text-xs uppercase text-muted-foreground">Ingreso bruto</div>
                            <div className="text-2xl font-semibold">${fmt(simTicket)}</div>
                        </div>
                        <div className="p-4 rounded-lg bg-slate-50 border">
                            <div className="text-xs uppercase text-muted-foreground">Tu neto* (aprox)</div>
                            <div className="text-2xl font-semibold">${fmt(sim.yourNet)}</div>
                            <div className="text-[10px] text-muted-foreground">*Antes de comisión de plataforma.</div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Stats */}
            <Card>
                <CardContent className="p-5">
                    <h3 className="text-base font-semibold mb-3">Tu impacto hasta hoy</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg border">
                            <div className="text-xs uppercase text-muted-foreground">Cashback entregado</div>
                            <div className="text-xl font-semibold">{fmt(stats.total_cashback_given)} pts</div>
                        </div>
                        <div className="p-3 rounded-lg border">
                            <div className="text-xs uppercase text-muted-foreground">Órdenes con cashback</div>
                            <div className="text-xl font-semibold">{stats.cashback_transactions}</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
